import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Ban,
  CheckCircle2,
  Copy,
  Download,
  ExternalLink,
  Mail,
  Pencil,
  RefreshCw,
  Send,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE, formatApiError, TOKEN_KEY } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useHasRole } from "@/components/RoleGate";
import { formatCurrency, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SUPPORTED_LANGUAGES } from "@/i18n";
import { STATUS_BADGE_CLASSES } from "@/pages/Invoices";

export default function InvoiceDetail() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canSendCancel = useHasRole("admin", "accountant");
  const canDelete = useHasRole("admin");
  const canRegenerate = useHasRole("admin", "accountant");
  const language = i18n.language?.split("-")[0] || "en";

  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [markPaidOpen, setMarkPaidOpen] = useState(false);
  const [paidAmount, setPaidAmount] = useState("");
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/invoices/${id}`);
      setInv(data);
      setPaidAmount(data.amount_due > 0 ? String(data.amount_due) : "");
    } catch (err) {
      toast.error(formatApiError(err, t("invoices.toasts.error")));
      navigate("/app/invoices");
    } finally {
      setLoading(false);
    }
  }, [id, navigate, t]);

  useEffect(() => {
    load();
  }, [load]);

  async function action(fn, successKey) {
    setBusy(true);
    try {
      await fn();
      if (successKey) toast.success(t(successKey));
      load();
    } catch (err) {
      toast.error(formatApiError(err, t("invoices.toasts.error")));
    } finally {
      setBusy(false);
    }
  }

  function publicUrl() {
    if (!inv) return "";
    return `${window.location.origin}/p/invoice/${inv.public_token}`;
  }

  async function copyPublicLink() {
    try {
      await navigator.clipboard.writeText(publicUrl());
      toast.success(t("invoices.toasts.linkCopied"));
    } catch {
      toast.error(t("invoices.toasts.error"));
    }
  }

  async function downloadPdf(lang) {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const url = `${API_BASE}/invoices/${inv.id}/pdf${lang ? `?lang=${lang}` : ""}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `${inv.invoice_number}${lang ? `_${lang}` : ""}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (err) {
      toast.error(formatApiError(err, t("invoices.toasts.error")));
    }
  }

  if (loading || !inv) {
    return <div className="text-sm text-slate-500" data-testid="invoice-detail-loading">{t("common.loading")}</div>;
  }

  const isDraft = inv.status === "draft";
  const isCancelled = inv.status === "cancelled";
  const isPaid = inv.status === "paid";
  const canEdit =
    isDraft &&
    (user?.role === "admin" || user?.role === "accountant" /* user role can edit own; backend enforces */);

  return (
    <div className="space-y-8" data-testid="invoice-detail-page">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/app/invoices")}
            data-testid="invoice-detail-back"
            className="gap-1 -ms-2"
          >
            <ArrowLeft className="h-4 w-4 rtl:-scale-x-100" />
            {t("invoices.backToList")}
          </Button>
          <div className="flex items-center gap-3">
            <h1 className="font-mono text-2xl tracking-tight text-slate-900">
              {inv.invoice_number}
            </h1>
            <Badge className={STATUS_BADGE_CLASSES[inv.status]} data-testid="invoice-detail-status">
              {t(`invoices.status.${inv.status}`)}
            </Badge>
            {inv.type === "credit" && (
              <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
                {t("invoices.type.credit")}
              </Badge>
            )}
          </div>
          {inv.last_email_sent_at && (
            <div className="text-xs text-slate-500" data-testid="invoice-detail-last-email">
              {t("invoices.detail.lastEmail", { date: formatDate(inv.last_email_sent_at, language) })}
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canEdit && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/app/invoices/${inv.id}/edit`)}
              data-testid="invoice-detail-edit"
              className="gap-1"
            >
              <Pencil className="h-4 w-4" />
              {t("common.edit")}
            </Button>
          )}
          {isDraft && canSendCancel && (
            <Button
              size="sm"
              onClick={() => action(() => api.post(`/invoices/${inv.id}/send`), "invoices.toasts.sent")}
              disabled={busy}
              data-testid="invoice-detail-send"
              className="gap-1 bg-slate-900 text-white hover:bg-slate-800"
            >
              <Send className="h-4 w-4 rtl:-scale-x-100" />
              {t("invoices.actions.send")}
            </Button>
          )}
          {!isDraft && !isCancelled && canSendCancel && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => action(() => api.post(`/invoices/${inv.id}/resend-email`), "invoices.toasts.emailResent")}
              disabled={busy}
              data-testid="invoice-detail-resend-email"
              className="gap-1 text-slate-700"
            >
              <Mail className="h-4 w-4" />
              {t("invoices.actions.resendEmail")}
            </Button>
          )}
          {!isDraft && !isCancelled && !isPaid && canSendCancel && (
            <Button
              size="sm"
              onClick={() => setMarkPaidOpen(true)}
              data-testid="invoice-detail-mark-paid"
              className="gap-1 bg-emerald-700 text-white hover:bg-emerald-800"
            >
              <CheckCircle2 className="h-4 w-4" />
              {t("invoices.actions.markPaid")}
            </Button>
          )}
          {!isCancelled && !isPaid && canSendCancel && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConfirmCancel(true)}
              data-testid="invoice-detail-cancel-btn"
              className="gap-1 text-amber-700"
            >
              <Ban className="h-4 w-4" />
              {t("invoices.actions.cancel")}
            </Button>
          )}
          {canDelete && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConfirmDelete(true)}
              data-testid="invoice-detail-delete"
              className="gap-1 text-red-600"
            >
              <Trash2 className="h-4 w-4" />
              {t("common.delete")}
            </Button>
          )}
        </div>
      </header>

      <section className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-white p-5 lg:col-span-2">
          <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                {t("invoices.detail.billTo")}
              </div>
              <div className="mt-1 text-base font-semibold text-slate-900">
                {inv.customer.name}
              </div>
              {inv.customer.email && <div className="text-sm text-slate-500">{inv.customer.email}</div>}
              {inv.customer.address && (
                <div className="mt-1 text-sm text-slate-500">
                  {inv.customer.address.street}<br/>
                  {inv.customer.address.postal_code} {inv.customer.address.city}<br/>
                  {inv.customer.address.country}
                </div>
              )}
              {inv.customer.tax_id && (
                <div className="mt-1 text-sm text-slate-500">
                  {t("invoices.detail.taxId")}: {inv.customer.tax_id}
                </div>
              )}
            </div>
            <div className="text-end space-y-1">
              <div className="text-xs uppercase tracking-wide text-slate-500">
                {t("invoices.detail.issued")}
              </div>
              <div className="text-sm">{formatDate(inv.issue_date, language)}</div>
              <div className="mt-2 text-xs uppercase tracking-wide text-slate-500">
                {t("invoices.detail.due")}
              </div>
              <div className="text-sm">{formatDate(inv.due_date, language)}</div>
            </div>
          </div>

          <div className="overflow-hidden rounded-md border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-3 py-2 text-start text-xs uppercase tracking-wide text-slate-500">
                    {t("invoices.detail.description")}
                  </th>
                  <th className="px-3 py-2 text-end text-xs uppercase tracking-wide text-slate-500">
                    {t("invoices.detail.qty")}
                  </th>
                  <th className="px-3 py-2 text-end text-xs uppercase tracking-wide text-slate-500">
                    {t("invoices.detail.price")}
                  </th>
                  <th className="px-3 py-2 text-end text-xs uppercase tracking-wide text-slate-500">
                    {t("invoices.detail.tax")}
                  </th>
                  <th className="px-3 py-2 text-end text-xs uppercase tracking-wide text-slate-500">
                    {t("invoices.detail.amount")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {inv.line_items.map((li, idx) => (
                  <tr key={idx} className="border-t border-slate-100">
                    <td className="px-3 py-3">
                      <div className="font-medium text-slate-900">{li.name}</div>
                      {li.description && <div className="text-xs text-slate-500">{li.description}</div>}
                      {li.unit && <div className="text-xs text-slate-400">{li.unit}</div>}
                    </td>
                    <td className="px-3 py-3 text-end tabular-nums">{li.quantity}</td>
                    <td className="px-3 py-3 text-end tabular-nums">{formatCurrency(li.unit_price, inv.currency, language)}</td>
                    <td className="px-3 py-3 text-end tabular-nums">{li.tax_rate}%</td>
                    <td className="px-3 py-3 text-end tabular-nums font-medium">{formatCurrency(li.total, inv.currency, language)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {inv.notes && (
            <div className="mt-4 text-sm text-slate-600 whitespace-pre-wrap">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t("invoices.detail.notes")}</div>
              <div>{inv.notes}</div>
            </div>
          )}

          {inv.reminders_sent && inv.reminders_sent.length > 0 && (
            <div className="mt-4" data-testid="invoice-detail-reminders">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                {t("invoices.detail.remindersHistory")}
              </div>
              <ul className="mt-2 space-y-1">
                {inv.reminders_sent.map((r, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-xs text-slate-600">
                    <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">
                      {t(`invoices.reminders.${r.type}`)}
                    </Badge>
                    <span>{formatDate(r.sent_at, language)}</span>
                    <Badge
                      className={
                        r.status === "sent"
                          ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100"
                          : r.status === "mocked"
                            ? "bg-amber-100 text-amber-800 hover:bg-amber-100"
                            : "bg-red-100 text-red-800 hover:bg-red-100"
                      }
                    >
                      {r.status}
                    </Badge>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">{t("invoices.totals.subtotal")}</span><span className="tabular-nums">{formatCurrency(inv.subtotal, inv.currency, language)}</span></div>
              {inv.discount_total > 0 && (
                <div className="flex justify-between"><span className="text-slate-500">{t("invoices.totals.discount")}</span><span className="tabular-nums">− {formatCurrency(inv.discount_total, inv.currency, language)}</span></div>
              )}
              <div className="flex justify-between"><span className="text-slate-500">{t("invoices.totals.tax")}</span><span className="tabular-nums">{formatCurrency(inv.tax_total, inv.currency, language)}</span></div>
              <div className="flex justify-between border-t border-slate-200 pt-2 font-semibold text-slate-900"><span>{t("invoices.totals.total")}</span><span className="tabular-nums" data-testid="invoice-detail-total">{formatCurrency(inv.total, inv.currency, language)}</span></div>
              {inv.amount_paid > 0 && (
                <div className="flex justify-between"><span className="text-slate-500">{t("invoices.totals.paid")}</span><span className="tabular-nums text-emerald-700">{formatCurrency(inv.amount_paid, inv.currency, language)}</span></div>
              )}
              {inv.amount_due > 0 && !isCancelled && (
                <div className="flex justify-between font-medium text-red-700"><span>{t("invoices.totals.due")}</span><span className="tabular-nums">{formatCurrency(inv.amount_due, inv.currency, language)}</span></div>
              )}
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {t("invoices.detail.pdf")}
            </div>
            <div className="flex flex-wrap gap-2">
              {SUPPORTED_LANGUAGES.map((lng) => (
                <Button
                  key={lng}
                  variant="outline"
                  size="sm"
                  onClick={() => downloadPdf(lng)}
                  data-testid={`invoice-detail-pdf-${lng}`}
                  className="gap-1"
                >
                  <Download className="h-3.5 w-3.5" />
                  {t(`languages.${lng}`)}
                </Button>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {t("invoices.detail.publicLink")}
            </div>
            <div className="flex items-center gap-2">
              <Input
                readOnly
                value={publicUrl()}
                onFocus={(e) => e.target.select()}
                data-testid="invoice-detail-public-url"
                className="font-mono text-xs"
              />
              <Button variant="outline" size="icon" onClick={copyPublicLink} data-testid="invoice-detail-copy">
                <Copy className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="icon" asChild>
                <Link to={`/p/invoice/${inv.public_token}`} target="_blank" data-testid="invoice-detail-open-public">
                  <ExternalLink className="h-4 w-4 rtl:-scale-x-100" />
                </Link>
              </Button>
            </div>
            <div className="text-xs text-slate-500">
              {t("invoices.detail.publicExpires", { date: formatDate(inv.public_token_expires_at, language) })}
            </div>
            {canRegenerate && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => action(() => api.post(`/invoices/${inv.id}/regenerate-public-link`), "invoices.toasts.linkRegenerated")}
                disabled={busy}
                data-testid="invoice-detail-regenerate"
                className="gap-1 text-slate-600"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                {t("invoices.actions.regenerate")}
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* Mark paid dialog */}
      <Dialog open={markPaidOpen} onOpenChange={setMarkPaidOpen}>
        <DialogContent data-testid="invoice-mark-paid-dialog">
          <DialogHeader>
            <DialogTitle>{t("invoices.markPaid.title")}</DialogTitle>
            <DialogDescription>{t("invoices.markPaid.description")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>{t("invoices.markPaid.amount")}</Label>
              <Input
                type="number"
                step="0.01"
                min={0}
                value={paidAmount}
                onChange={(e) => setPaidAmount(e.target.value)}
                data-testid="invoice-mark-paid-amount"
              />
              <div className="text-xs text-slate-500">
                {t("invoices.markPaid.due")}: {formatCurrency(inv.amount_due, inv.currency, language)}
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t("invoices.markPaid.method")}</Label>
              <Select defaultValue="bank_transfer">
                <SelectTrigger data-testid="invoice-mark-paid-method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_transfer">{t("invoices.markPaid.methods.bank_transfer")}</SelectItem>
                  <SelectItem value="card">{t("invoices.markPaid.methods.card")}</SelectItem>
                  <SelectItem value="cash">{t("invoices.markPaid.methods.cash")}</SelectItem>
                  <SelectItem value="other">{t("invoices.markPaid.methods.other")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMarkPaidOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              onClick={async () => {
                await action(
                  () => api.post(`/invoices/${inv.id}/mark-paid`, { amount: Number(paidAmount), method: "bank_transfer" }),
                  "invoices.toasts.paid"
                );
                setMarkPaidOpen(false);
              }}
              disabled={busy || !paidAmount}
              data-testid="invoice-mark-paid-confirm"
              className="bg-emerald-700 text-white hover:bg-emerald-800"
            >
              {t("invoices.markPaid.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel confirmation */}
      <AlertDialog open={confirmCancel} onOpenChange={setConfirmCancel}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("invoices.cancel.title")}</AlertDialogTitle>
            <AlertDialogDescription>{t("invoices.cancel.description")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                action(() => api.post(`/invoices/${inv.id}/cancel`), "invoices.toasts.cancelled")
              }
              data-testid="invoice-cancel-confirm"
              className="bg-amber-600 text-white hover:bg-amber-700"
            >
              {t("invoices.cancel.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirmation */}
      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isDraft ? t("invoices.delete.titleDraft") : t("invoices.delete.titleNonDraft")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isDraft ? t("invoices.delete.descriptionDraft") : t("invoices.delete.descriptionNonDraft")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                try {
                  const { data } = await api.delete(`/invoices/${inv.id}`);
                  // Backend returns {hard: true} for draft hard-deletes and
                  // {hard: false, status: 'cancelled'} for soft-cancels.
                  if (data && data.hard === false) {
                    toast.success(t("invoices.toasts.cancelled"));
                  } else {
                    toast.success(t("invoices.toasts.deleted"));
                  }
                  navigate("/app/invoices");
                } catch (err) {
                  toast.error(formatApiError(err, t("invoices.toasts.error")));
                }
              }}
              data-testid="invoice-delete-confirm"
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {isDraft ? t("common.delete") : t("invoices.actions.cancel")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
